import { batch, createSignal } from 'solid-js'
import { createStore, produce, unwrap } from 'solid-js/store'
import {
  applyDelta,
  capStack,
  deltaEqual,
  snapshotDelta,
  type HistoryEntry,
  type SceneDelta,
} from './history.ts'
import { newId } from './factory.ts'
import { emptyScene, type Camera, type NodeId, type Scene, type SceneNode } from './types.ts'

const [scene, setScene] = createStore<Scene>(emptyScene())
export { scene }

const [undoStack, setUndoStack] = createSignal<HistoryEntry[]>([])
const [redoStack, setRedoStack] = createSignal<HistoryEntry[]>([])
const [selection, setSelection] = createSignal<NodeId[]>([])
const [dirty, setDirty] = createSignal(false)

export const canUndo = () => undoStack().length > 0
export const canRedo = () => redoStack().length > 0
export const isDirty = dirty
export const markClean = () => setDirty(false)
export const markDirty = () => setDirty(true)

// ---------------------------------------------------------------- history

interface PendingCommit {
  ids: Set<NodeId>
  includeOrder: boolean
  before: SceneDelta
}

let pending: PendingCommit | null = null

/**
 * Open a commit before a gesture starts. Everything the gesture touches must be
 * named up front — a snapshot taken mid-gesture would already be the new value.
 */
export function beginCommit(ids: Iterable<NodeId>, includeOrder = false): void {
  const idSet = new Set(ids)
  pending = {
    ids: idSet,
    includeOrder,
    before: snapshotDelta(unwrap(scene), idSet, includeOrder),
  }
}

export function endCommit(label: string): void {
  if (!pending) return
  const { ids, includeOrder, before } = pending
  pending = null
  const after = snapshotDelta(unwrap(scene), ids, includeOrder)
  if (deltaEqual(before, after)) return
  setUndoStack((s) => capStack([...s, { label, before, after, at: Date.now() }]))
  setRedoStack([])
  setDirty(true)
}

export function abortCommit(): void {
  pending = null
}

export function isCommitOpen(): boolean {
  return pending !== null
}

export function commit(
  label: string,
  ids: Iterable<NodeId>,
  fn: () => void,
  includeOrder = false,
): void {
  beginCommit(ids, includeOrder)
  batch(fn)
  endCommit(label)
}

export function undo(): void {
  const stack = undoStack()
  const entry = stack[stack.length - 1]
  if (!entry) return
  batch(() => {
    applyDelta(setScene, entry.before)
    setUndoStack(stack.slice(0, -1))
    setRedoStack((r) => capStack([...r, entry]))
    pruneSelection()
    setDirty(true)
  })
}

export function redo(): void {
  const stack = redoStack()
  const entry = stack[stack.length - 1]
  if (!entry) return
  batch(() => {
    applyDelta(setScene, entry.after)
    setRedoStack(stack.slice(0, -1))
    setUndoStack((u) => capStack([...u, entry]))
    pruneSelection()
    setDirty(true)
  })
}

export function resetHistory(): void {
  pending = null
  setUndoStack([])
  setRedoStack([])
}

// ---------------------------------------------------------------- document

export function loadScene(next: Scene): void {
  batch(() => {
    setScene(structuredClone(next))
    setSelection([])
    resetHistory()
    setDirty(false)
  })
}

export function snapshotScene(): Scene {
  return structuredClone(unwrap(scene))
}

export function nodeById(id: NodeId): SceneNode | undefined {
  return scene.nodes[id]
}

export function orderedNodes(): SceneNode[] {
  return scene.order.map((id) => scene.nodes[id]).filter((n): n is SceneNode => !!n)
}

/**
 * Insert without recording history. Drag-to-create tools use this inside their
 * own open commit so creating and sizing a shape is a single undo step.
 */
export function insertNodes(nodes: SceneNode[]): void {
  setScene(
    produce((s) => {
      for (const n of nodes) {
        s.nodes[n.id] = structuredClone(n)
        s.order.push(n.id)
      }
    }),
  )
  setDirty(true)
}

/** Remove without recording history. */
export function removeNodes(ids: NodeId[]): void {
  const doomed = new Set(ids)
  setScene(
    produce((s) => {
      for (const id of doomed) delete s.nodes[id]
      s.order = s.order.filter((id) => !doomed.has(id))
    }),
  )
  setSelection((sel) => sel.filter((id) => !doomed.has(id)))
  setDirty(true)
}

export function addNodes(nodes: SceneNode[], label = 'Add'): void {
  if (nodes.length === 0) return
  commit(label, nodes.map((n) => n.id), () => insertNodes(nodes), true)
}

export function deleteNodes(ids: NodeId[], label = 'Delete'): void {
  const present = ids.filter((id) => scene.nodes[id])
  if (present.length === 0) return
  commit(label, present, () => removeNodes(present), true)
}

/** Fresh ids, shifted by `offset`; copies form their own groups rather than joining the originals'. */
export function cloneNodes(nodes: readonly SceneNode[], offset: number): SceneNode[] {
  const groupCopies = new Map<string, string>()
  return nodes.map((n) => {
    const copy: SceneNode = {
      ...structuredClone(unwrap(n)),
      id: newId(),
      x: n.x + offset,
      y: n.y + offset,
    }
    if (n.groupId) {
      if (!groupCopies.has(n.groupId)) groupCopies.set(n.groupId, newId())
      copy.groupId = groupCopies.get(n.groupId)
    }
    return copy
  })
}

/** Add copies of `nodes` and select them, as one undo step. */
export function pasteNodes(nodes: readonly SceneNode[], offset: number, label: string): void {
  const copies = cloneNodes(nodes, offset)
  if (copies.length === 0) return
  addNodes(copies, label)
  setSelection(copies.map((c) => c.id))
}

export function duplicateSelection(offset = 16): void {
  pasteNodes(selectedNodes(), offset, 'Duplicate')
}

export function nudgeSelection(dx: number, dy: number): void {
  const nodes = selectedNodes()
  if (nodes.length === 0) return
  commit('Nudge', nodes.map((n) => n.id), () => {
    for (const n of nodes) updateNode(n.id, { x: n.x + dx, y: n.y + dy })
  })
}

/** Live, fine-grained property write. Does not record history by itself. */
export function updateNode(id: NodeId, patch: Partial<SceneNode>): void {
  if (!scene.nodes[id]) return
  setScene('nodes', id, patch as never)
  setDirty(true)
}

export function setCamera(camera: Camera): void {
  setScene('camera', camera)
  setDirty(true)
}

/** Not in undo history: deltas only cover nodes and order. */
export function setBackground(color: string): void {
  setScene('background', color)
  setDirty(true)
}

// ---------------------------------------------------------------- groups

export function groupSelection(): void {
  const ids = selection()
  if (ids.length < 2) return
  const groupId = newId()
  const members = new Set(ids)
  // Gathered at the topmost member's slot so the layers panel can show the
  // group as one block.
  const top = Math.max(...ids.map((id) => scene.order.indexOf(id)))
  const order = [
    ...scene.order.slice(0, top + 1).filter((id) => !members.has(id)),
    ...sortByOrder(ids),
    ...scene.order.slice(top + 1),
  ]
  commit(
    'Group',
    ids,
    () => {
      for (const id of ids) updateNode(id, { groupId })
      setScene('order', order)
    },
    true,
  )
}

export function ungroupSelection(): void {
  const ids = selection().filter((id) => scene.nodes[id]?.groupId)
  if (ids.length === 0) return
  commit('Ungroup', ids, () => {
    for (const id of ids) updateNode(id, { groupId: undefined })
  })
}

/** Expand ids to every selectable member of the groups they belong to. */
export function withGroups(ids: NodeId[]): NodeId[] {
  const groups = new Set(ids.map((id) => scene.nodes[id]?.groupId).filter((g) => g))
  if (groups.size === 0) return ids
  const members = scene.order.filter((id) => {
    const n = scene.nodes[id]
    return !!n && !n.locked && !n.hidden && groups.has(n.groupId)
  })
  return [...new Set([...ids, ...members])]
}

// ---------------------------------------------------------------- z-order

function reorder(mutator: (order: NodeId[]) => NodeId[], label: string): void {
  commit(label, [], () => setScene('order', mutator([...scene.order])), true)
}

export function bringToFront(ids: NodeId[]): void {
  reorder((order) => [...order.filter((id) => !ids.includes(id)), ...sortByOrder(ids)], 'Bring to front')
}

export function sendToBack(ids: NodeId[]): void {
  reorder((order) => [...sortByOrder(ids), ...order.filter((id) => !ids.includes(id))], 'Send to back')
}

export function bringForward(ids: NodeId[]): void {
  reorder((order) => shift(order, ids, 1), 'Bring forward')
}

export function sendBackward(ids: NodeId[]): void {
  reorder((order) => shift(order, ids, -1), 'Send backward')
}

/** Move the node at `from` to index `to` in paint order. */
export function moveInOrder(id: NodeId, to: number): void {
  reorder((order) => {
    const from = order.indexOf(id)
    if (from === -1) return order
    order.splice(from, 1)
    order.splice(Math.max(0, Math.min(order.length, to)), 0, id)
    return order
  }, 'Reorder')
}

function sortByOrder(ids: NodeId[]): NodeId[] {
  return [...ids].sort((a, b) => scene.order.indexOf(a) - scene.order.indexOf(b))
}

/** Step the given ids one slot up or down, walking from the end they move toward. */
function shift(order: NodeId[], ids: NodeId[], dir: 1 | -1): NodeId[] {
  const indices = ids
    .map((id) => order.indexOf(id))
    .filter((i) => i !== -1)
    .sort((a, b) => (dir === 1 ? b - a : a - b))
  for (const i of indices) {
    const j = i + dir
    if (j < 0 || j >= order.length) continue
    if (ids.includes(order[j]!)) continue
    ;[order[i], order[j]] = [order[j]!, order[i]!]
  }
  return order
}

// ---------------------------------------------------------------- selection

export { selection }

/**
 * Deliberately a plain derivation rather than a memo. A memo with no current
 * observer does not recompute, and most callers here are event handlers outside
 * any tracking scope — they would read a stale selection.
 */
export const selectedNodes = (): SceneNode[] =>
  selection()
    .map((id) => scene.nodes[id])
    .filter((n): n is SceneNode => !!n)

export function select(ids: NodeId[]): void {
  setSelection(ids.filter((id) => scene.nodes[id] && !scene.nodes[id]!.locked))
}

export function addToSelection(ids: NodeId[]): void {
  setSelection((sel) => [...new Set([...sel, ...ids])])
}

export function toggleSelection(id: NodeId): void {
  setSelection((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]))
}

export function clearSelection(): void {
  setSelection([])
}

export function selectAll(): void {
  setSelection(scene.order.filter((id) => !scene.nodes[id]?.locked && !scene.nodes[id]?.hidden))
}

function pruneSelection(): void {
  setSelection((sel) => sel.filter((id) => scene.nodes[id]))
}
